import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import moment from 'moment';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';

const PAGE_CACHE_MODIFIER = 20; // Modifier for the query interval per page
const DEFAULT_ZOOM_LEVEL = 10;

/**
 * `Timeline` is a component for showing events in a horizontal calendar.
 *
 * The component will fire an event saying which interval it is currently caching,
 * this is the interval you should fetch the data for
 *
 * Also exports the function that is used to calculate vertical offset / height of each
 * event as well as the default DEFAULT_ZOOM_LEVEL constant
 *
 * @property {object} startTime A moment object that declares where the timeline should start.
 * @property {function} setStartTime Function to set the external state of the start time whenever it is updated by the component.
 * @property {array} events Array of groups that will be displayed on the timeline
 * @property {function} getItemInterval Function to get the start and end moments on the
 * form { start: ..., end: ... }
 * @property {function} itemRenderer Function that will be used to produce the DOM for
 * a single item, accepts React Component Classes or pure functions that return jsx
 * @property {function} groupRenderer Function that will be used to produce the DOM for
 * a single group, see sidebar, accepts React Component Classes or pure functions that return jsx
 * @property {number} zoomLevel Number used to calculate how much space each minute on the
 * calendar should use. eg. 10 is the default
 * @property {object} start A moment that declares where the timeline should start.
 * @property {function} onChangePage Handle for when the cache interval has been changed
 * @property {function} onChangeDate Handle for when the display data has been changed
 * @property {function} constraints Start and end moments if you'd like to restrict scrolling
 * indefinently
 * @property {function} onCollapseSidebar Shows handle and call callback when triggered
 * @property {bool} collapsedSidebar
 *
 * @extends React Component
 * @see Timeline
 */
export default class Timeline extends Component {
  static displayName = 'Timeline';

  static getOffset = function (
    start,
    end,
    relativeTo,
    zoomLevel = DEFAULT_ZOOM_LEVEL
  ) {
    const left =
      moment.duration(start.diff(relativeTo)).asMinutes() * zoomLevel;
    const width = moment.duration(end.diff(start)).asMinutes() * zoomLevel;
    return { left, width };
  };

  static propTypes = {
    itemRenderer: PropTypes.func.isRequired, // eslint-disable-line
    groupRenderer: PropTypes.func.isRequired, // eslint-disable-line
    sidebarHeaderRenderer: PropTypes.func.isRequired,
    startTime: PropTypes.object.isRequired, // moment object
    setStartTime: PropTypes.func,
    zoomLevel: PropTypes.number.isRequired,
    groups: PropTypes.array.isRequired,
    getItemInterval: PropTypes.func.isRequired,
    onChangePage: PropTypes.func.isRequired,
    onChangeDate: PropTypes.func.isRequired,
    itemsKey: PropTypes.string.isRequired, // eslint-disable-line
    className: PropTypes.string,
    range: PropTypes.object,
    duration: PropTypes.number,
    constraints: PropTypes.object,
    utcOffset: PropTypes.number,
    onCollapseSidebar: PropTypes.func,
    collapsedSidebar: PropTypes.bool,
  };

  /* eslint-disable react/default-props-match-prop-types */
  static defaultProps = {
    startTime: moment(),
    utcOffset: 0,
    itemsKey: 'items',
    zoomLevel: DEFAULT_ZOOM_LEVEL, // 10px per minute,
    getItemInterval: (object) => ({ start: object.start, end: object.end }),
    itemRenderer: ({ item, group }) => (
      <div data-testid="timeslot">
        {item.start.format('HH:mm')} - {item.end.format('HH:mm')}
      </div>
    ),
    groupRenderer: ({ group }) => (
      <div data-testid="timeslot-group">{group.name}</div>
    ),
    /**
     * @todo Implement onChangePage function
     */
    onChangePage: (startDate, endDate) => {},
    sidebarHeaderRenderer: () => null,
    /**
     * @todo Implement onChangeDate function
     */
    onChangeDate: (currentDay) => {},
    /**
     * @todo Implement onCollapseSidebar function
     */
    onCollapseSidebar: () => {},
    collapsedSidebar: false,
  };
  /* eslint-enable react/default-props-match-prop-types */

  constructor(props) {
    super(props);
    this.state = {
      xOffset: 0,
      yOffset: 0,
      markers: [],
      currentTime: moment(),
      windowStart: props.startTime,
    };
    this.dimension = {
      width: 0,
      height: 0,
    };

    this.currentDate = props.startTime;
    this.viewDuration = 0;
    this.pageDuration = 0;
    this.scrollXDelta = 0;
    this.scrollYDelta = 0;
    this.currentPageStart = 0;
    this.currentPageEnd = 0;
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleOnResize);

    this.calculateBoundaries();
    this.fetchData();
    this.generateMarkers();
    this.startTimer();
  }

  componentDidUpdate(prevProps) {
    // scroll to top if data length changes
    if (prevProps.groups.length !== this.props.groups.length) {
      this.scrollYDelta = 0;
      this.setState({ yOffset: 0 });
    }

    if (!moment(prevProps.startTime).isSame(this.props.startTime)) {
      this.scrollXDelta = this.timelineContent.clientWidth / 2;
      this.setState(
        {
          windowStart: this.props.startTime,
          xOffset: this.timelineContent.clientWidth / 2
        },
        () => {
          this.updateCurrentDate({ fireOnChangeDate: false });
          this.fetchDataIfNeeded();
        }
      );
    }

    this.calculateBoundaries();
    if (prevProps.zoomLevel !== this.props.zoomLevel) {
      this.fetchData();
      this.generateMarkers();
      this.startTimer();
      // fetch data
    }
  }

  componentWillUnmount() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    window.removeEventListener('resize', this.handleOnResize);
  }

  getCenterTime = () => {
    const { xOffset } = this.state;
    const { zoomLevel } = this.props;
    const { windowStart } = this.state;
    const centerOffsetMinutesFromStart =
      xOffset / zoomLevel - this.viewDuration / 2;
    const centerTime = windowStart
      .clone()
      .subtract(centerOffsetMinutesFromStart, 'minutes');
    return centerTime;
  };

  getNextDataInterval = () => {
    const centerTime = this.getCenterTime();
    const start = centerTime.clone().subtract(this.pageDuration / 2, 'minutes'); // half of the interval before center
    const end = centerTime.clone().add(this.pageDuration / 2, 'minutes'); // half of the interval after center
    return { start, end };
  };

  getPosition = (start, end) => {
    const { zoomLevel, range } = this.props;
    const { windowStart } = this.state;
    if (range) {
      return { left: 0 + start * zoomLevel, width: (end - start) * zoomLevel };
    }
    const left =
      moment.duration(start.diff(windowStart)).asMinutes() * zoomLevel;
    const width = moment.duration(end.diff(start)).asMinutes() * zoomLevel;
    return { left, width };
  };

  getIsInRange = () => {
    const viewEnd = this.getCenterTime()
      .clone()
      .add(this.viewDuration / 2);
    const viewStart = this.getCenterTime()
      .clone()
      .subtract(this.viewDuration / 2);
    if (
      viewStart.isBefore(this.currentPageStart) ||
      viewEnd.isAfter(this.currentPageEnd)
    ) {
      return false;
    }
    return true;
  };

  getTimelineIntervalObject = (range, lastElementLeft, durationValue) => {
    const indicator = (lastElementLeft / range.end) * durationValue;
    return { indicatorLeft: indicator };
  };

  startTimer = () => {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    this.timerId = setInterval(() => {
      this.setState({ currentTime: moment() });
    }, 10000);
  };

  fetchData() {
    const { onChangePage } = this.props;
    const { start, end } = this.getNextDataInterval();
    this.currentPageStart = start;
    this.currentPageEnd = end;
    onChangePage(start, end);
  }

  calculateBoundaries = () => {
    const { zoomLevel, constraints } = this.props;
    this.dimension = {
      width: this.timelineRef.clientWidth,
      height: this.timelineRef.clientHeight,
    };
    this.yOffsetMax = Math.max(
      Math.max(
        this.timelineContent.clientHeight,
        this.timelineSidebar.clientHeight
      ) - this.dimension.height,
      0
    );
    if (constraints) {
      this.xOffsetMax = 0;
      this.xOffsetMin =
        Timeline.getOffset(
          constraints.end,
          constraints.start,
          constraints.start
        ).width + this.timelineRef.clientWidth;
    }
    this.viewDuration = this.dimension.width / zoomLevel;
    this.pageDuration = this.viewDuration * PAGE_CACHE_MODIFIER;
  };

  fetchDataIfNeeded = () => {
    const isInRange = this.getIsInRange();
    if (!isInRange) {
      this.fetchData();
      this.generateMarkers();
    }
  };

  calculateItemPosition = (item) => {
    const { getItemInterval } = this.props;
    const { start, end } = getItemInterval(item);
    return this.getPosition(start, end);
  };

  generateMarkers = () => {
    const { range, zoomLevel } = this.props;
    if (range) {
      const markerArr = [];
      const endStyle = range.end * zoomLevel;
      let counter = 10;
      while (counter * zoomLevel < endStyle) {
        markerArr.push({ start: counter, end: counter + 10 });
        counter += 10;
      }
      this.setState({ markers: markerArr });
    } else {
      const centerTime = this.getCenterTime();
      const numberOfMarkers = Math.ceil(this.pageDuration / 30); // a marker every 30 minutes
      const markerStart = centerTime
        .clone()
        .subtract(this.pageDuration / 2, 'minutes');
      markerStart.milliseconds(0);
      markerStart.seconds(0);
      markerStart.minutes(0);
      const markerArr = new Array(numberOfMarkers);
      let idx = 0;
      while (idx < numberOfMarkers) {
        const next = markerStart.clone().add(idx * 30, 'minutes');
        markerArr[idx] = { start: next, end: next };
        idx += 1;
      }
      this.setState({ markers: markerArr });
    }
  };

  updateCurrentDate = ({fireOnChangeDate} = {fireOnChangeDate: true}) => {
    const { onChangeDate } = this.props;
    const { currentDate } = this;
    const centerTime = this.getCenterTime();
    if (
      centerTime.month() !== currentDate.month() ||
      centerTime.day() !== currentDate.day()
    ) {
      this.currentDate = centerTime;
      fireOnChangeDate && onChangeDate(centerTime);
    }
  };

  handleOnResize = () => {
    this.calculateBoundaries();
    this.fetchData();
    this.generateMarkers();
  };

  handleOnWheel = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const { deltaX, deltaY } = event;
    const { range, zoomLevel, constraints } = this.props;

    const newX = this.scrollXDelta - deltaX;
    const newY = this.scrollYDelta - deltaY;

    if (!constraints || (newX <= this.xOffsetMax && newX >= this.xOffsetMin)) {
      this.scrollXDelta = newX;
    }

    this.scrollYDelta = newY;

    if (this.scrollYDelta - deltaY < -this.yOffsetMax) {
      this.scrollYDelta = -this.yOffsetMax;
    }

    if (this.scrollYDelta > 0) {
      this.scrollYDelta = 0;
    }

    if (range) {
      if (
        this.scrollXDelta <=
        -(range.end * zoomLevel) + this.timelineContent.clientWidth
      ) {
        this.scrollXDelta =
          -(range.end * zoomLevel) + this.timelineContent.clientWidth;
      } else if (this.scrollXDelta >= 0) {
        this.scrollXDelta = 0;
      }
    }

    this.setState(
      { xOffset: this.scrollXDelta, yOffset: this.scrollYDelta },
      () => {
        this.updateCurrentDate();
        this.fetchDataIfNeeded();
      }
    );
  };

  handleOnNextInterval = () => {
    const { constraints } = this.props;
    const currentCenter = this.getCenterTime();
    let nextStart = currentCenter
      .clone()
      .add(this.viewDuration * 0.5, 'minutes');
    if (
      constraints &&
      nextStart
        .clone()
        .add(this.viewDuration, 'minutes')
        .isAfter(constraints.end)
    ) {
      nextStart = constraints.end
        .clone()
        .subtract(this.viewDuration, 'minutes');
    }
    this.scrollXDelta = 0;
    this.setState({ xOffset: 0, windowStart: nextStart }, () => {
      this.updateCurrentDate();
      this.fetchDataIfNeeded();
    });
  };

  handleOnPrevInterval = () => {
    const { constraints } = this.props;
    const currentCenter = this.getCenterTime();
    let nextStart = currentCenter
      .clone()
      .subtract(this.viewDuration * 1.5, 'minutes');
    if (constraints && nextStart.isBefore(constraints.start)) {
      nextStart = constraints.start.clone();
    }
    this.scrollXDelta = 0;
    this.setState({ xOffset: 0, windowStart: nextStart }, () => {
      this.updateCurrentDate();
      this.fetchDataIfNeeded();
    });
  };

  renderContentBackground = () => {
    const { groups } = this.props;

    return groups.map((group, groupIdx) => (
      <div key={groupIdx} className="core-timeline-content-group-bg" />
    ));
  };

  render() {
    const { xOffset, yOffset, markers, currentTime } = this.state;
    const {
      range,
      zoomLevel,
      duration,
      utcOffset,
      sidebarHeaderRenderer,
      onCollapseSidebar,
      collapsedSidebar,
    } = this.props;
    let currentTimePosition;
    if (range) {
      currentTimePosition = this.getTimelineIntervalObject(
        range,
        range.end * zoomLevel,
        duration
      ).indicatorLeft;
    } else {
      currentTimePosition = this.getPosition(currentTime, currentTime).left;
    }

    const classes = cx(
      'core-timeline',
      {
        'collapsable-sidebar': onCollapseSidebar,
        'collapsed-sidebar': collapsedSidebar,
      },
      this.props.className
    );

    return (
      <div className={classes} onWheel={this.handleOnWheel}>
        <div className="core-timeline-left" data-testid="left-timeline">
          <div className="core-timeline-sidebar">
            <div
              style={{ transform: `translate3d(0, ${yOffset}px, 0)` }}
              className="core-timeline-sidebar-groups"
              ref={(c) => {
                this.timelineSidebar = c;
              }}>
              <SidebarData {...this.props} />
            </div>
            {onCollapseSidebar ? (
              <div className="collapse-handle" onClick={onCollapseSidebar}>
                <Icon name={collapsedSidebar ? 'arrow-right' : 'arrow-left'} />
              </div>
            ) : null}
            <div className="core-timeline-sidebar-header">
              {sidebarHeaderRenderer()}
            </div>
          </div>
        </div>

        <div
          className="core-timeline-right"
          ref={(c) => {
            this.timelineRef = c;
          }}
          data-testid="right-timeline">
          <div
            style={{ transform: `translate3d(0, ${yOffset}px, 0)` }}
            className="core-timeline-content-bg">
            {this.renderContentBackground()}
          </div>

          <div
            style={{ transform: `translate3d(${xOffset}px, ${yOffset}px, 0)` }}
            className="core-timeline-content"
            ref={(c) => {
              this.timelineContent = c;
            }}>
            <TimelineData
              {...this.props}
              windowStart={this.state.windowStart}
              calculateItemPosition={this.calculateItemPosition}
            />
          </div>

          <div className="core-timeline-markers-bg" />

          <div
            style={{ transform: `translate3d(${xOffset}px, 0, 0)` }}
            className="core-timeline-markers">
            <TimelineMarkers
              {...this.props}
              utcOffset={utcOffset}
              windowStart={this.state.windowStart}
              markers={markers}
              getPosition={this.getPosition}
            />
          </div>

          <div
            className="core-timeline-indicator"
            style={{
              left: currentTimePosition,
              transform: `translate3d(${xOffset}px, 0, 0)`,
            }}>
            <div className="core-timeline-indicator-line" />
            <div className="core-timeline-indicator-header">
              {range ? duration.toFixed(2) : currentTime.format('HH:mm')}
            </div>
          </div>
          {range ? (
            ''
          ) : (
            <div className="core-timeline-marker-actions">
              <Button
                iconName="arrow-left"
                onClick={this.handleOnPrevInterval}
              />
              <Button
                iconName="arrow-right"
                onClick={this.handleOnNextInterval}
              />
            </div>
          )}
          <div className="core-timeline-background">
            <span />
          </div>
        </div>
      </div>
    );
  }
}

class SidebarData extends Component {
  static propTypes = {
    groups: PropTypes.array.isRequired,
    groupRenderer: PropTypes.func.isRequired,
  };

  shouldComponentUpdate(nextProps) {
    return nextProps.groups !== this.props.groups;
  }

  renderSidebarGroups = () => {
    const Group = this.props.groupRenderer;
    const { groups } = this.props;

    return groups.map((group, groupIdx) => (
      <div key={groupIdx} className="core-timeline-sidebar-group">
        <Group group={group} />
      </div>
    ));
  };

  render() {
    return (
      <div className="core-timeline-sidebar-data">
        {this.renderSidebarGroups()}
      </div>
    );
  }
}

class TimelineMarkers extends Component {
  static propTypes = {
    markers: PropTypes.array.isRequired,
    getPosition: PropTypes.func.isRequired,
    zoomLevel: PropTypes.number.isRequired,
    windowStart: PropTypes.object.isRequired,
    utcOffset: PropTypes.number.isRequired,
    range: PropTypes.object,
  };

  shouldComponentUpdate(nextProps) {
    return (
      nextProps.markers !== this.props.markers ||
      nextProps.zoomLevel !== this.props.zoomLevel ||
      nextProps.windowStart !== this.props.windowStart
    );
  }

  renderMarkers() {
    const { markers, range, zoomLevel, utcOffset } = this.props;
    if (range) {
      const startStyle = range.start * zoomLevel;
      const endStyle = range.end * zoomLevel;

      return (
        <div className="core-timeline-markers">
          <div className="core-timeline-marker" style={{ left: startStyle }}>
            {range.start}
          </div>
          {markers.map((marker, markerIdx) => {
            const style = this.props.getPosition(marker.start, marker.end);
            return (
              <div
                key={markerIdx}
                className="core-timeline-marker"
                style={{ left: style.left }}>
                {marker.start}
              </div>
            );
          })}
          <div className="core-timeline-marker" style={{ left: endStyle }}>
            {range.end}
          </div>
        </div>
      );
    }
    return (
      <div className="core-timeline-markers">
        {markers.map((marker, markerIdx) => {
          const style = this.props.getPosition(marker.start, marker.end);
          return (
            <div
              key={markerIdx}
              className="core-timeline-marker"
              style={{ left: style.left }}>
              {marker.start.clone().add(utcOffset, 'minutes').format('HH:mm')}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <div className="core-timeline-markers-data">{this.renderMarkers()}</div>
    );
  }
}

class TimelineData extends Component {
  static propTypes = {
    itemRenderer: PropTypes.func.isRequired,
    itemsKey: PropTypes.string.isRequired,
    groups: PropTypes.array.isRequired,
    zoomLevel: PropTypes.number.isRequired,
    calculateItemPosition: PropTypes.func.isRequired,
    windowStart: PropTypes.object.isRequired,
  };

  shouldComponentUpdate(nextProps) {
    return (
      nextProps.groups !== this.props.groups ||
      nextProps.zoomLevel !== this.props.zoomLevel ||
      nextProps.windowStart !== this.props.windowStart
    );
  }

  renderItem(item, idx, group) {
    const Item = this.props.itemRenderer;
    const style = this.props.calculateItemPosition(item);

    return (
      <div key={idx} className="core-timeline-content-item" style={style}>
        <Item item={item} group={group} />
      </div>
    );
  }

  renderGroup = (group, groupIdx) => (
    <div key={groupIdx} className="core-timeline-content-group">
      {group[this.props.itemsKey].map((item, itemIdx) =>
        this.renderItem(item, itemIdx, group)
      )}
    </div>
  );

  render() {
    const { groups } = this.props;

    return (
      <div className="core-timeline-data">
        {groups.map((group, groupIdx) => this.renderGroup(group, groupIdx))}
      </div>
    );
  }
}
